import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api/axios';
import './LeadBulkActionsDrawer.css';

function nameOf(lead) { return lead.fullName || lead.leadName || [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Naamloos'; }
function filteredParams(search) {
  const source = new URLSearchParams(search);
  const allowed = ['search','accepted','replied','hasEmail','crmProvider','crmSyncStatus','crmOpenOpportunity','crmDoNotContact','crmRecentActivityDays','campaignId','campaignFit','fitMinScore','fitMaxScore','sortBy','sortDir'];
  const params = {};
  for (const key of allowed) if (source.get(key)) params[key] = source.get(key);
  params.page = 1; params.limit = 100;
  return params;
}

export default function LeadBulkActionsDrawer({ onChanged }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [options, setOptions] = useState({ leadLists: [], labels: [], campaigns: [] });
  const [action, setAction] = useState('add_labels');
  const [labelValue, setLabelValue] = useState('');
  const [listId, setListId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [leadsResponse, optionsResponse] = await Promise.all([
        api.get('/leads/workspace', { params: filteredParams(location.search) }),
        api.get('/leads/workspace/bulk/options'),
      ]);
      setRows(leadsResponse.data?.items || []);
      setOptions(optionsResponse.data || { leadLists: [], labels: [], campaigns: [] });
      setSelected(new Set());
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally { setLoading(false); }
  }, [location.search]);

  useEffect(() => { if (open) load(); }, [open, load]);
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((lead) => String(lead._id))));
  const toggle = (id) => setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const needsLabels = action === 'add_labels' || action === 'remove_labels';
  const needsList = action === 'add_to_list' || action === 'remove_from_list';
  const needsCampaign = action === 'add_to_campaign' || action === 'stop_outreach';
  const canRun = selected.size > 0 && ((needsLabels && labelValue.trim()) || (needsList && listId) || (needsCampaign && campaignId));
  const selectedRows = useMemo(() => rows.filter((lead) => selected.has(String(lead._id))), [rows, selected]);

  const run = async () => {
    if (!canRun) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const payload = { action, leadIds: [...selected] };
      if (needsLabels) payload.labels = labelValue.split(',').map((value) => value.trim()).filter(Boolean);
      if (needsList) payload.listId = listId;
      if (needsCampaign) payload.campaignId = campaignId;
      const response = await api.post('/leads/workspace/bulk', payload);
      if (action === 'stop_outreach') {
        const stopped = Number(response.data?.stopped || 0);
        const skipped = Number(response.data?.skippedNotInCampaign || 0);
        const proposals = Number(response.data?.proposalsCanceled || 0);
        const tasks = Number(response.data?.tasksCanceled || 0);
        setNotice(`${stopped} leads gestopt · ${proposals} voorstellen en ${tasks} geplande acties ingetrokken${skipped ? ` · ${skipped} zaten niet in deze campagne` : ''}.`);
      } else {
        const skipped = Number(response.data?.skippedExisting || 0);
        setNotice(`${response.data?.modified || 0} leads bijgewerkt${skipped ? ` · ${skipped} zaten al in de campagne` : ''}.`);
      }
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally { setBusy(false); }
  };

  return <>
    <button className="lead-bulk-launcher" onClick={() => setOpen(true)}>Bulk selecteren</button>
    {open && <div className="lead-bulk-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
      <aside className="lead-bulk-drawer">
        <header><div><p>Bulkacties</p><h2>Selecteer leads</h2><span>De huidige Leads-filters zijn overgenomen. Maximaal 100 zichtbare matches per batch.</span></div><button onClick={() => setOpen(false)}>×</button></header>
        {error && <div className="lead-bulk-error">{error}</div>}
        {notice && <div className="lead-bulk-notice">{notice}</div>}
        <section className="lead-bulk-toolbar">
          <label><input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!rows.length} /><span>{selected.size ? `${selected.size} geselecteerd` : 'Alles op deze batch selecteren'}</span></label>
          <button onClick={load} disabled={loading || busy}>Vernieuwen</button>
        </section>
        <div className="lead-bulk-list">
          {loading ? <div className="lead-bulk-empty">Leads laden…</div> : rows.map((lead) => <label key={lead._id} className={selected.has(String(lead._id)) ? 'selected' : ''}>
            <input type="checkbox" checked={selected.has(String(lead._id))} onChange={() => toggle(String(lead._id))} />
            <div><strong>{nameOf(lead)}</strong><span>{[lead.jobTitle || lead.title, lead.organization || lead.company, lead.email].filter(Boolean).join(' · ')}</span></div>
          </label>)}
          {!loading && !rows.length && <div className="lead-bulk-empty">Geen leads binnen de huidige filters.</div>}
        </div>
        <footer>
          <div className="lead-bulk-summary"><strong>{selected.size}</strong><span>{selected.size === 1 ? 'lead' : 'leads'} klaar voor actie</span>{selectedRows.length > 0 && <small>{selectedRows.slice(0, 3).map(nameOf).join(', ')}{selectedRows.length > 3 ? ` +${selectedRows.length - 3}` : ''}</small>}</div>
          <label><span>Actie</span><select value={action} onChange={(event) => setAction(event.target.value)}><option value="add_to_campaign">Aan campagne toevoegen</option><option value="stop_outreach">Outreach stoppen</option><option value="add_labels">Label toevoegen</option><option value="remove_labels">Label verwijderen</option><option value="add_to_list">Aan Lead List toevoegen</option><option value="remove_from_list">Uit Lead List verwijderen</option></select></label>
          {needsCampaign && <label><span>Campagne</span><select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}><option value="">Kies een campagne…</option>{(options.campaigns || []).map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.title} · {campaign.status}</option>)}</select><small>{action === 'stop_outreach' ? 'Openstaande voorstellen en nog niet gestarte LinkedIn/e-mailacties worden ingetrokken. Een reeds processing taak wordt door de last-second guard geblokkeerd.' : 'Nieuwe leads krijgen automatisch een initieel agentdoel en worden klaar gezet voor planning.'}</small></label>}
          {action === 'stop_outreach' && <div className="lead-bulk-warning"><strong>Outreach wordt voor deze campagne gestopt.</strong><span>Dit zet Lead Memory en het actieve agentdoel op stop_outreach. De leads blijven gewoon in de campagne en in je CRM/Lead Lists.</span></div>}
          {needsLabels && <label><span>Labels</span><input value={labelValue} onChange={(event) => setLabelValue(event.target.value)} list="lead-bulk-labels" placeholder="bijv. VIP, Benelux" /><datalist id="lead-bulk-labels">{(options.labels || []).map((item) => <option key={item.label} value={item.label} />)}</datalist><small>Meerdere labels scheiden met komma's.</small></label>}
          {needsList && <label><span>Lead List</span><select value={listId} onChange={(event) => setListId(event.target.value)}><option value="">Kies een lijst…</option>{(options.leadLists || []).map((list) => <option key={list.id} value={list.id}>{list.title}</option>)}</select></label>}
          <button className="lead-bulk-run" disabled={!canRun || busy} onClick={run}>{busy ? 'Uitvoeren…' : `${action === 'stop_outreach' ? 'Outreach stoppen voor' : 'Actie uitvoeren op'} ${selected.size} leads`}</button>
        </footer>
      </aside>
    </div>}
  </>;
}
